import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Clock, Copy, ImagePlus, Loader2, MapPin, Package, Rocket, Save, Send, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { LocationPicker } from "@/components/LocationPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSeo } from "@/hooks/useSeo";
import { useHref, useLang, useT } from "@/i18n";
import { useCategoryLabel } from "@/i18n/categories";

export default function ManageListing() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const href = useHref();
  const t = useT();
  const lang = useLang();
  const catLabel = useCategoryLabel();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  // A verified card claim arrives as ?claim=<token> from the emailed link.
  const [token] = useState(() => {
    const fromLink = params.get("claim");
    if (fromLink) localStorage.setItem(`dirbiz_claim_${listingId}`, fromLink);
    return fromLink || localStorage.getItem(`dirbiz_claim_${listingId}`);
  });
  // Owners land here straight after verifying — offer the Featured upgrade once.
  const [justClaimed] = useState(() => Boolean(params.get("claim") || location.state?.justClaimed));
  const [upsellClosed, setUpsellClosed] = useState(false);
  const [buyingGallery, setBuyingGallery] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("1m");
  const [renewPlan] = useState(() => params.get("renew"));
  const autoRenewed = useRef(false);

  useSeo({ title: t("manage.seo.title") });

  // Plan labels come from the backend in Malay ("1 bulan").
  const planLabel = (p) =>
    !p ? "" : lang === "en" ? p.label.replace(/bulan/i, p.key === "1m" ? "month" : "months") : p.label;

  useEffect(() => {
    if (!params.get("claim")) return;
    // Drop only the secret, keep any companion params (e.g. ?renew=6m) intact.
    const next = new URLSearchParams(params);
    next.delete("claim");
    setParams(next, { replace: true });
  }, [params, setParams]);
  const [locations, setLocations] = useState({ states: {}, categories: [] });
  const [settings, setSettings] = useState(null);
  const [listing, setListing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [denied, setDenied] = useState(false);
  const photoRef = useRef(null);

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const gallery = useMemo(
    () =>
      listing?.gallery || {
        unlocked: false,
        used: (listing?.photos || []).length,
        limit: settings?.gallery_free_photos ?? 1,
        free_limit: settings?.gallery_free_photos ?? 1,
        paid_limit: settings?.max_photos ?? 7,
        price_label: settings?.gallery_price_label || "RM10",
      },
    [listing, settings]
  );

  const offer = useMemo(
    () =>
      listing?.featured_offer || {
        price_label: settings?.featured_price_label || "RM10",
        normal_price_label: settings?.featured_price_label || "RM10",
        discounted: false,
      },
    [listing, settings]
  );

  const plans = useMemo(
    () => offer.plans || settings?.featured_plans || [],
    [offer, settings]
  );
  const selectedPricing = useMemo(
    () => plans.find((p) => p.key === selectedPlan) || plans[0] || null,
    [plans, selectedPlan]
  );

  const daysLeft = useMemo(() => {
    if (!listing?.featured_until) return null;
    const ms = new Date(listing.featured_until) - new Date();
    return ms > 0 ? Math.ceil(ms / 86400000) : 0;
  }, [listing]);

  // One-click renew from the expiry reminder email: /manage/{id}?claim=...&renew=6m
  useEffect(() => {
    if (!renewPlan || !listing || autoRenewed.current) return;
    autoRenewed.current = true;
    setSelectedPlan(renewPlan);
    toast.info(t("manage.toast.renewing"));
    promote(renewPlan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renewPlan, listing]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/listings/${listingId}/owner`, authHeaders);
      setListing(data);
      setForm({
        phone_number: data.phone_number || "",
        category: data.category || "",
        state: data.state || "",
        district: data.district || "",
        street_address: data.street_address || "",
        postcode: data.postcode || "",
        description: data.description || "",
        lat: data.lat ?? null,
        lng: data.lng ?? null,
      });
    } catch {
      setDenied(true);
    }
  }, [listingId, authHeaders]);

  useEffect(() => {
    if (!token) {
      setDenied(true);
      return;
    }
    api.get("/locations").then(({ data }) => setLocations(data));
    api.get("/settings/public").then(({ data }) => setSettings(data));
    load();
  }, [token, load]);

  if (denied) {
    return (
      <div className="mx-auto max-w-xl px-4 py-24 text-center sm:px-8">
        <h1 data-testid="manage-denied" className="font-display text-2xl font-bold tracking-tight">
          {t("manage.denied.title")}
        </h1>
        <p className="mt-3 text-sm text-slate-600">
          {t("manage.denied.body")}
        </p>
        <Button
          data-testid="manage-goto-claim-button"
          onClick={() => navigate(href("/claim"))}
          className="mt-8 bg-primary hover:bg-[#0052CC]"
        >
          {t("manage.denied.cta")}
        </Button>
      </div>
    );
  }

  if (!listing || !form) {
    return (
      <div className="grid h-80 place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const set = (key, value) =>
    setForm((f) => ({ ...f, [key]: value, ...(key === "state" ? { district: "" } : {}) }));
  const districts = form.state ? locations.states?.[form.state] || [] : [];

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put(`/listings/${listingId}/owner`, form, authHeaders);
      setListing(data);
      toast.success(t("manage.toast.saved"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const promote = async (planKey = selectedPlan, bundle = false) => {
    setPromoting(true);
    try {
      const { data } = await api.post(`/listings/${listingId}/feature`, {}, {
        ...authHeaders,
        params: { plan: planKey, bundle },
      });
      if (data.payment_url) window.location.assign(data.payment_url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPromoting(false);
    }
  };

  const addPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post(`/listings/${listingId}/photos`, body, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setListing((l) => ({ ...l, photos: data.photos }));
      toast.success(t("manage.toast.photoAdded"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  };

  const buyGallery = async () => {
    setBuyingGallery(true);
    try {
      const { data } = await api.post(`/listings/${listingId}/gallery`, {}, authHeaders);
      if (data.payment_url) window.location.assign(data.payment_url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBuyingGallery(false);
    }
  };

  const removePhoto = async (url) => {
    try {
      const { data } = await api.delete(`/listings/${listingId}/photos`, {
        ...authHeaders,
        params: { photo_url: url },
      });
      setListing((l) => ({ ...l, photos: data.photos }));
      toast.success(t("manage.toast.photoRemoved"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const movePhoto = async (index, direction) => {
    const before = [...(listing.photos || [])];
    const photos = [...before];
    const target = index + direction;
    if (target < 0 || target >= photos.length) return;
    [photos[index], photos[target]] = [photos[target], photos[index]];
    setListing((l) => ({ ...l, photos }));
    try {
      await api.put(`/listings/${listingId}/photos/order`, { photos }, authHeaders);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setListing((l) => ({ ...l, photos: before }));
    }
  };

  const shareText = t("manage.share.msg", {
    name: listing.business_name,
    category: listing.category,
    district: listing.district,
    state: listing.state,
    phone: listing.phone_number,
    url: `${process.env.REACT_APP_BACKEND_URL}/api/share/${listing.slug || listingId}`,
  });

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-8">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t("manage.eyebrow")}</p>
      <h1 data-testid="manage-business-name" className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
        {listing.business_name}
      </h1>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Badge variant="secondary" className="text-[11px]">{listing.status}</Badge>
        {listing.is_featured && (
          <Badge data-testid="manage-featured-badge" className="bg-amber-100 text-amber-900 text-[11px]">
            <Star className="mr-1 h-3 w-3" /> Featured
          </Badge>
        )}
        <Link to={href(`/business/${listing.slug || listingId}`)} data-testid="manage-view-public-link" className="text-sm text-primary hover:underline">
          {t("manage.viewPublic")}
        </Link>
      </div>

      <div className="mt-5 flex flex-wrap gap-3" data-testid="manage-share-panel">
        <a
          href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="manage-share-whatsapp"
          className="inline-flex items-center gap-2 rounded-md bg-[#25D366] px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#1DA851]"
        >
          <Send className="h-4 w-4" /> {t("manage.share.whatsapp")}
        </a>
        <button
          type="button"
          data-testid="manage-share-copy"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(shareText);
              toast.success(t("manage.share.copied"));
            } catch {
              toast.error(t("manage.share.copyFail"));
            }
          }}
          className="inline-flex items-center gap-2 rounded-md border border-border px-5 py-3 text-sm font-semibold text-slate-800 transition-colors duration-200 hover:bg-slate-900 hover:text-white"
        >
          <Copy className="h-4 w-4" /> {t("manage.share.copy")}
        </button>
        <p className="w-full text-xs text-slate-500">
          {t("manage.share.note")}
        </p>
      </div>

      {justClaimed && !upsellClosed && !listing.is_featured && (
        <section
          data-testid="claim-upsell-panel"
          className="mt-8 overflow-hidden rounded-lg border border-amber-400 bg-slate-950 p-7 text-white shadow-lg"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-amber-300">{t("manage.upsell.eyebrow")}</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl">
            {t("manage.upsell.title", { price: offer.price_label || "RM10" })}
            {offer.discounted && (
              <span className="ml-3 align-middle text-base font-medium text-slate-400 line-through">
                {offer.normal_price_label}
              </span>
            )}
          </h2>
          {offer.discounted && (
            <p
              data-testid="claim-upsell-offer-countdown"
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-amber-400/15 px-3 py-1.5 text-sm font-semibold text-amber-300"
            >
              <Clock className="h-4 w-4" />
              {t("manage.upsell.countdown", { hours: offer.hours_left })}
            </p>
          )}
          <ul className="mt-4 space-y-2 text-sm leading-relaxed text-slate-200">
            <li className="flex items-start gap-2">
              <Star className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
              {t("manage.upsell.b1", { days: settings?.featured_days || 30 })}
            </li>
            <li className="flex items-start gap-2">
              <Star className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
              {t("manage.upsell.b2")}
            </li>
            <li className="flex items-start gap-2">
              <Star className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
              {t("manage.upsell.b3")}
            </li>
          </ul>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              data-testid="claim-upsell-promote-button"
              disabled={promoting}
              onClick={() => promote("1m")}
              className="h-12 bg-amber-400 px-7 font-semibold text-slate-950 transition-colors duration-200 hover:bg-amber-300"
            >
              {promoting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Rocket className="mr-2 h-4 w-4" />}
              {t("manage.upsell.cta", { price: offer.price_label || "RM10" })}
            </Button>
            <button
              type="button"
              data-testid="claim-upsell-dismiss-button"
              onClick={() => setUpsellClosed(true)}
              className="text-sm font-medium text-slate-400 transition-colors duration-200 hover:text-white sm:ml-2"
            >
              {t("manage.upsell.later")}
            </button>
          </div>
        </section>
      )}

      {!(justClaimed && !upsellClosed && !listing.is_featured) && (
      <section
        data-testid="promote-panel"
        className="mt-8 rounded-lg border border-amber-300 bg-amber-50 p-6"
      >        <div className="flex flex-wrap items-center justify-between gap-5">
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight text-amber-950">{t("manage.promote.title")}</h2>
            {listing.is_featured && (
              <span
                data-testid="featured-days-left-badge"
                className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                  daysLeft <= 7 ? "bg-red-600 text-white" : "bg-amber-950 text-amber-100"
                }`}
              >
                <Clock className="h-3.5 w-3.5" />
                {t("manage.promote.daysLeft", { days: daysLeft, date: String(listing.featured_until).slice(0, 10) })}
              </span>
            )}
            <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-amber-900">
              {listing.is_featured
                ? t("manage.promote.activeBody", { date: String(listing.featured_until).slice(0, 10) })
                : t("manage.promote.idleBody")}
            </p>
            {offer.discounted && !listing.is_featured && (
              <p
                data-testid="promote-offer-badge"
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-amber-950 px-3 py-1.5 text-xs font-semibold text-amber-100"
              >
                <Clock className="h-3.5 w-3.5" />
                {t("manage.promote.offer", { price: offer.price_label, normal: offer.normal_price_label, hours: offer.hours_left })}
              </p>
            )}
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-3" data-testid="featured-plans">
          {plans.map((p) => {
            const active = selectedPlan === p.key;
            const price = p.key === "1m" && offer.discounted && !listing.is_featured
              ? offer.price_label
              : p.price_label;
            return (
              <button
                type="button"
                key={p.key}
                data-testid={`featured-plan-${p.key}`}
                onClick={() => setSelectedPlan(p.key)}
                className={`rounded-lg border p-4 text-left transition-colors duration-200 ${
                  active
                    ? "border-amber-900 bg-amber-950 text-amber-50"
                    : "border-amber-300 bg-white text-amber-950 hover:border-amber-900"
                }`}
              >
                <p className="text-xs uppercase tracking-[0.15em] opacity-70">{planLabel(p)}</p>
                <p className="mt-1.5 font-display text-2xl font-bold tracking-tight">{price}</p>
                <p className="mt-1 text-xs opacity-80">{t("manage.promote.daysTop", { days: p.days })}</p>
                {p.key === "1m" && offer.discounted && !listing.is_featured && (
                  <p className="mt-1 text-xs font-semibold">
                    <span className="line-through opacity-70">{p.price_label}</span> {t("manage.promote.newOwnerOffer")}
                  </p>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button
            data-testid="promote-button"
            disabled={promoting}
            onClick={() => promote(selectedPlan)}
            className="bg-amber-950 text-amber-50 transition-colors duration-200 hover:bg-amber-900"
          >
            {promoting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Rocket className="mr-2 h-4 w-4" />}
            {t("manage.promote.pay", {
              price: selectedPricing
                ? selectedPlan === "1m" && offer.discounted && !listing.is_featured
                  ? offer.price_label
                  : selectedPricing.price_label
                : offer.price_label,
            })}
          </Button>
          <p className="text-xs text-amber-900">{t("manage.promote.note")}</p>
        </div>

        {selectedPricing && !gallery.unlocked && (
          <div
            data-testid="bundle-panel"
            className="mt-6 rounded-lg border border-amber-900 bg-white p-5"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="inline-flex items-center gap-2 rounded-full bg-amber-950 px-3 py-1 text-xs font-semibold text-amber-100">
                  <Package className="h-3.5 w-3.5" /> {t("manage.bundle.badge", { saving: selectedPricing.bundle_saving_label })}
                </p>
                <p className="mt-3 font-display text-lg font-bold tracking-tight text-amber-950">
                  {t("manage.bundle.title", { label: planLabel(selectedPricing), photos: gallery.paid_limit })}
                  <span data-testid="bundle-price">{selectedPricing.bundle_price_label}</span>
                </p>
                <p className="mt-1 text-sm text-amber-900">
                  {t("manage.bundle.body", {
                    separate: selectedPricing.bundle_separate_label,
                    featured: selectedPricing.price_label,
                    gallery: gallery.price_label,
                  })}
                </p>
              </div>
              <Button
                data-testid="bundle-buy-button"
                disabled={promoting}
                onClick={() => promote(selectedPlan, true)}
                className="bg-amber-500 font-semibold text-amber-950 transition-colors duration-200 hover:bg-amber-400"
              >
                <Package className="mr-2 h-4 w-4" />
                {t("manage.bundle.cta", { price: selectedPricing.bundle_price_label })}
              </Button>
            </div>
          </div>
        )}
      </section>
      )}

      <section className="mt-10" data-testid="gallery-panel">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight">{t("manage.gallery.title")}</h2>
            <p className="mt-1.5 text-sm text-slate-600">
              {gallery.unlocked
                ? t("manage.gallery.unlocked", { limit: gallery.paid_limit })
                : t("manage.gallery.locked", { free: gallery.free_limit, paid: gallery.paid_limit })}
            </p>
            <p className="mt-1 text-xs font-semibold text-slate-500" data-testid="gallery-usage">
              {t("manage.gallery.usage", { used: gallery.used, limit: gallery.limit })}
            </p>
          </div>
          <input
            ref={photoRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={addPhoto}
            className="hidden"
            data-testid="gallery-file-input"
          />
          <Button
            type="button"
            variant="outline"
            data-testid="gallery-upload-button"
            disabled={uploading || gallery.used >= gallery.limit}
            onClick={() => photoRef.current?.click()}
          >
            {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-2 h-4 w-4" />}
            {t("manage.gallery.add")}
          </Button>
        </div>

        {!gallery.unlocked && (
          <div
            data-testid="gallery-upgrade-panel"
            className="mt-5 rounded-lg border border-sky-200 bg-sky-50 p-5"
          >
            <p className="font-display text-base font-bold tracking-tight text-sky-950">
              {t("manage.gallery.upgradeTitle", { price: gallery.price_label })}
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-sky-900">
              {t("manage.gallery.upgradeBody", { photos: gallery.paid_limit })}
            </p>
            <Button
              data-testid="gallery-upgrade-button"
              disabled={buyingGallery}
              onClick={buyGallery}
              className="mt-4 bg-sky-900 text-white hover:bg-sky-800"
            >
              {buyingGallery ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ImagePlus className="mr-2 h-4 w-4" />
              )}
              {t("manage.gallery.upgradeCta", { price: gallery.price_label })}
            </Button>
          </div>
        )}

        {(listing.photos || []).length === 0 ? (
          <p
            data-testid="gallery-empty"
            className="mt-5 rounded-lg border border-dashed border-border p-8 text-center text-sm text-slate-500"
          >
            {t("manage.gallery.empty")}
          </p>
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
            {listing.photos.map((p, i) => (
              <div
                key={p}
                data-testid={`gallery-item-${i}`}
                className="group relative aspect-[4/3] overflow-hidden rounded-lg border border-border bg-slate-100"
              >
                <img src={mediaUrl(p)} alt={t("manage.gallery.photoAlt", { n: i + 1 })} className="h-full w-full object-cover" />
                <button
                  type="button"
                  data-testid={`gallery-delete-${i}`}
                  onClick={() => removePhoto(p)}
                  className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-md bg-slate-900/80 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100 focus:opacity-100"
                  aria-label={t("manage.gallery.remove")}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                {i === 0 && (
                  <span className="absolute left-2 top-2 rounded bg-slate-900/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                    {t("manage.gallery.main")}
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 flex justify-between gap-2 bg-gradient-to-t from-slate-900/80 to-transparent p-2">
                  <button
                    type="button"
                    data-testid={`gallery-move-left-${i}`}
                    disabled={i === 0}
                    onClick={() => movePhoto(i, -1)}
                    className="grid h-8 w-8 place-items-center rounded-md bg-white/90 text-slate-900 transition-colors duration-200 hover:bg-white disabled:opacity-30"
                    aria-label={t("manage.gallery.moveLeft")}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    data-testid={`gallery-move-right-${i}`}
                    disabled={i === (listing.photos || []).length - 1}
                    onClick={() => movePhoto(i, 1)}
                    className="grid h-8 w-8 place-items-center rounded-md bg-white/90 text-slate-900 transition-colors duration-200 hover:bg-white disabled:opacity-30"
                    aria-label={t("manage.gallery.moveRight")}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <form onSubmit={save} className="mt-10 grid gap-5">        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <Label htmlFor="m-phone">{t("manage.form.phone")}</Label>
            <Input
              id="m-phone"
              data-testid="manage-phone-input"
              value={form.phone_number}
              onChange={(e) => set("phone_number", e.target.value)}
              className="mt-2 h-11"
            />
          </div>
          <div>
            <Label>{t("manage.form.category")}</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="mt-2 h-11" data-testid="manage-category-select">
                <SelectValue placeholder={t("submit.select.category")} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {(locations.categories || []).map((c) => (
                  <SelectItem key={c} value={c} data-testid={`manage-category-option-${c}`}>
                    {catLabel(c)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <Label>{t("manage.form.state")}</Label>
            <Select value={form.state} onValueChange={(v) => set("state", v)}>
              <SelectTrigger className="mt-2 h-11" data-testid="manage-state-select">
                <SelectValue placeholder={t("submit.select.state")} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {Object.keys(locations.states || {}).map((s) => (
                  <SelectItem key={s} value={s} data-testid={`manage-state-option-${s}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("manage.form.district")}</Label>
            <Select value={form.district} onValueChange={(v) => set("district", v)} disabled={!form.state}>
              <SelectTrigger className="mt-2 h-11" data-testid="manage-district-select">
                <SelectValue placeholder={t("submit.select.district")} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {districts.map((d) => (
                  <SelectItem key={d} value={d} data-testid={`manage-district-option-${d}`}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <Label htmlFor="m-address">{t("manage.form.address")}</Label>
            <Input
              id="m-address"
              data-testid="manage-address-input"
              placeholder="No 12, Jalan Besar"
              value={form.street_address}
              onChange={(e) => set("street_address", e.target.value)}
              className="mt-2 h-11"
            />
          </div>
          <div>
            <Label htmlFor="m-postcode">{t("manage.form.postcode")}</Label>
            <Input
              id="m-postcode"
              data-testid="manage-postcode-input"
              inputMode="numeric"
              placeholder="06000"
              value={form.postcode}
              onChange={(e) => set("postcode", e.target.value.replace(/\D/g, "").slice(0, 5))}
              className="mt-2 h-11"
            />
          </div>
        </div>
        <p className="-mt-2 text-xs text-slate-500">
          {t("manage.form.addressNote")}
        </p>

        <div data-testid="manage-location-field">
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">
            <MapPin className="mr-1 inline h-3.5 w-3.5" /> {t("manage.form.map")}
          </Label>
          <LocationPicker
            address={form.street_address}
            postcode={form.postcode}
            district={form.district}
            state={form.state}
            value={form.lat && form.lng ? { lat: form.lat, lng: form.lng } : null}
            onChange={(p) => setForm((f) => ({ ...f, lat: p.lat, lng: p.lng }))}
          />
        </div>


        <div>
          <Label htmlFor="m-desc">{t("manage.form.description")}</Label>
          <Textarea
            id="m-desc"
            data-testid="manage-description-input"
            rows={3}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            className="mt-2"
          />
        </div>

        <Button
          type="submit"
          data-testid="manage-save-button"
          disabled={saving}
          className="h-12 bg-primary hover:bg-[#0052CC] sm:w-fit sm:px-8"
        >
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          {t("manage.form.save")}
        </Button>
      </form>
    </div>
  );
}
